import { Theme } from '@emotion/react';
import { Box, IconButton, Stack, SxProps, Tooltip } from '@mui/material';
import FundInvestmentInvestor from 'FundInvestmentData';
import React from 'react';
import { Navigate } from 'react-router-dom';
import Contact from '../../models/Contact';
import InvestmentEntityContact from '../../models/InvestmentEntityContact';
import Investor from '../../models/Investor';
import ExtendedLiquid from '../../services/ExtendedLiquid';
import { Palette } from 'Palette';
import { StyledAvatar, StyledCard, StyledTypography } from './styles';
import type { CardViewLinkHrefs } from '../admin/CardViewLinks';

interface BaseModelCardProps {
  backgroundColor?: string;
}

export const generateActionButton = (
  tooltip: string,
  icon: React.ReactNode,
  onClick: () => void,
) => {
  return (
    <Tooltip title={tooltip}>
      <IconButton
        onClick={onClick}
        sx={{
          width: '20px',
          height: '20px',
          backgroundColor: 'rgba(255,255,255,0.9)',
          color: 'rgba(0,0,0,0.8)',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,1)',
            color: 'rgba(0,0,0,1)',
          },
          '& .MuiSvgIcon-root': {
            fontSize: '0.7rem',
          },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
};

export interface ModelCardProps extends BaseModelCardProps {
  name?: string;
  email?: string;
  phone?: string;
  investment?: number | null;
  entity: FundInvestmentInvestor | Investor | InvestmentEntityContact | Contact;
  tone?: keyof typeof Palette;
  onPromote?: () => void;
  onDemote?: () => void;
  onDelete?: () => void;
  contactId?: string;
  promotionStageLabel?: string;
  demotionStageLabel?: string;
  className?: string;
  onClick?: () => void;
  actionButtons?: React.ReactNode;
  hideViewButtons?: boolean;
  showOverlay?: boolean;
  contactOnly?: boolean;
  sx?: SxProps<Theme>;
  minHeight?: string | number;
  allowWrap?: boolean;
  icon?: React.ReactNode;
  viewLinks?: CardViewLinkHrefs;
}

interface ModelCardState {
  navigateContact: boolean;
  navigateInvestor: boolean;
  navigateFund: boolean;
}

export abstract class BaseModelCard extends React.Component<ModelCardProps, ModelCardState> {
  constructor(props: ModelCardProps) {
    super(props);
    this.state = {
      navigateContact: false,
      navigateInvestor: false,
      navigateFund: false,
    };
  }

  handleNavigateContact = () => {
    this.setState({ navigateContact: true });
  };

  handleNavigateInvestor = () => {
    this.setState({ navigateInvestor: true });
  };

  handleNavigateFund = () => {
    this.setState({ navigateFund: true });
  };

  /** Fund pipeline cards carry `fundOpportunityId` on `FundInvestmentInvestor` / `Investor`. */
  protected getFundOpportunityExternalId(): string | undefined {
    const { entity } = this.props;
    if (!entity || typeof entity !== 'object') {
      return undefined;
    }
    const e = entity as FundInvestmentInvestor & { fundOpportunityId?: unknown };
    if (typeof e.fundOpportunityId === 'string' && e.fundOpportunityId.trim().length > 0) {
      return e.fundOpportunityId.trim();
    }
    return undefined;
  }

  protected shouldShowOverlay(): boolean {
    return false; // Default implementation
  }

  protected renderOverlay(): React.ReactNode {
    return null; // Default implementation
  }

  protected renderInlineActions(): React.ReactNode {
    return null;
  }

  protected inlineActionsSx(): Record<string, unknown> {
    return {};
  }

  render() {
    const {
      name,
      email,
      phone,
      entity,
      investment,
      tone,
      contactId,
      backgroundColor,
      sx,
      minHeight,
      allowWrap,
      icon,
    } = this.props;

    // JavaScript-compatible detection for different entity types
    const isContact =
      entity &&
      typeof entity === 'object' &&
      'firstName' in entity &&
      'lastName' in entity &&
      'email' in entity &&
      !('contact' in entity);
    const isInvestmentEntityContact =
      entity && typeof entity === 'object' && 'contact' in entity && 'role' in entity;

    // Extract contact info based on entity type
    let contactName = name || 'Unnamed';
    let contactEmail = email || '-';
    let contactPhone = phone || '-';
    let role: string | undefined;
    let representativeType: string | undefined;
    let otherRepresentativeType: string | undefined;

    if (isInvestmentEntityContact) {
      const investmentEntityContact = entity as InvestmentEntityContact;
      const contact = investmentEntityContact.contact;
      contactEmail = contact.email || '-';
      contactPhone = contact.mobile || phone || '-';
      role = investmentEntityContact.role;
      representativeType = investmentEntityContact.representativeType;
      otherRepresentativeType = investmentEntityContact.otherRepresentativeType;
    } else if (isContact) {
      const contact = entity as unknown as Contact;
      contactName = `${contact.firstName} ${contact.lastName}`;
      contactEmail = contact.email || '-';
      contactPhone = contact.mobile || phone || '-';
    }

    if (this.state.navigateContact) {
      return <Navigate to={`/contacts/${contactId}`} replace />;
    }

    if (this.state.navigateInvestor) {
      return (
        <Navigate
          to={`/opportunities/${(entity as FundInvestmentInvestor)?.fundOpportunityId}/investors/${(entity as FundInvestmentInvestor)?.id}`}
          replace
        />
      );
    }

    if (this.state.navigateFund) {
      const fundId = this.getFundOpportunityExternalId();
      if (fundId) {
        return <Navigate to={`/opportunities/${fundId}`} replace />;
      }
    }

    // Safely determine if overlay should be shown
    const shouldShowOverlay = this.shouldShowOverlay();
    const computedFundOpportunityTitle = (entity as Investor)?.fundOpportunity?.name;

    return (
      <Box
        sx={{
          position: 'relative',
          cursor: 'pointer',
          width: '100%',
          boxSizing: 'border-box',
          ...this.inlineActionsSx(),
          ...(shouldShowOverlay && {
            '&:hover .action-overlay': {
              opacity: 1,
              visibility: 'visible',
            },
          }),
          ...sx,
        }}
        className={this.props.className}
      >
        <StyledCard
          tone={tone}
          className="react-ContactCard"
          sx={{
            padding: '0.5rem 0.75rem !important', // Increased padding: 8px 12px
            marginBottom: '3px',
            width: '100%',
            maxWidth: '100%',
            minWidth: '0', // Allow shrinking
            overflow: 'hidden', // Keep text contained within card
            backgroundColor: backgroundColor,
            minHeight: minHeight || '2.8125rem', // Use optional minHeight or default to 45px
            height: minHeight ? 'auto' : undefined, // Allow auto height when custom minHeight is set
            boxSizing: 'border-box', // Include padding in width calculation
            '& .MuiCardContent-root': {
              width: '100%',
              maxWidth: '100%',
              minWidth: '0',
              overflow: 'hidden', // Keep text contained within card
              padding: '0 !important', // Remove default padding
            },
            ...sx,
          }}
        >
          <Stack spacing={0.125} sx={{ width: '100%' }}>
            {/* Name row with icon or avatar */}
            <Stack direction="row" spacing={0.25} alignItems="center">
              {icon ? (
                <Box
                  sx={{
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: tone ? Palette[tone]?.text || 'inherit' : 'inherit',
                  }}
                >
                  {icon}
                </Box>
              ) : (
                <StyledAvatar
                  tone={tone}
                  sx={{ width: '16px', height: '16px', fontSize: '0.5rem' }}
                >
                  {contactName[0]?.toUpperCase() || '?'}
                </StyledAvatar>
              )}
              <StyledTypography
                fontWeight={700}
                tone={tone}
                sx={{
                  fontSize: '0.6rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: allowWrap ? undefined : 'nowrap',
                  flex: 1,
                }}
              >
                {contactName}
              </StyledTypography>
              {this.renderInlineActions()}
            </Stack>

            {computedFundOpportunityTitle && (
              <StyledTypography
                variant="body2"
                tone={tone}
                sx={{
                  fontSize: '0.5rem',
                  opacity: 0.9,
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: allowWrap ? 'clip' : 'ellipsis',
                  whiteSpace: allowWrap ? 'normal' : 'nowrap',
                  marginBottom: '2px',
                }}
              >
                {computedFundOpportunityTitle}
              </StyledTypography>
            )}

            {/* Email and other details - full width */}
            <StyledTypography
              variant="body2"
              tone={tone}
              sx={{
                fontSize: '0.55rem',
                opacity: 0.8,
                overflow: 'hidden',
                textOverflow: allowWrap ? 'clip' : 'ellipsis',
                whiteSpace: allowWrap ? 'normal' : 'nowrap',
              }}
            >
              {contactEmail}
            </StyledTypography>

            <StyledTypography variant="body2" tone={tone} sx={{ display: 'none' }}>
              {contactPhone}
            </StyledTypography>

            {/* Show role if it's a Contact with role info */}
            {role && (
              <StyledTypography
                variant="body2"
                tone={tone}
                sx={{
                  fontSize: '0.45rem',
                  opacity: 0.7,
                  fontStyle: 'italic',
                  overflow: 'hidden',
                  textOverflow: allowWrap ? 'clip' : 'ellipsis',
                  whiteSpace: allowWrap ? 'normal' : 'nowrap',
                }}
              >
                {role}
                {representativeType &&
                  ` - ${representativeType === 'OTHER' ? otherRepresentativeType : representativeType}`}
              </StyledTypography>
            )}

            {investment && (
              <StyledTypography
                mt={0.125}
                fontWeight={500}
                tone={tone}
                sx={{
                  fontSize: '0.45rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: allowWrap ? undefined : 'nowrap',
                }}
              >
                {ExtendedLiquid.currencyMillions(investment, true)}
              </StyledTypography>
            )}
          </Stack>
        </StyledCard>

        {/* Hover Overlay */}
        {shouldShowOverlay && (
          <Box
            className="action-overlay"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.85)',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              opacity: 0,
              visibility: 'hidden',
              transition: 'opacity 0.2s ease',
              zIndex: 10,
              width: '100%',
              height: '100%',
              margin: 0,
              padding: 0,
              boxSizing: 'border-box',
            }}
          >
            {this.renderOverlay()}
          </Box>
        )}
      </Box>
    );
  }
}

export default BaseModelCard;
